import './mailCodeActivationPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import {
  EMAIL_ADDRESS_PATTERN,
  PERSON_NAME_PATTERN,
  PHONE_NUMBER_PATTERN,
} from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import trim from 'lodash/trim';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { LanderImage } from '../../../components/LanderImage/LanderImage';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useAnalyticStore from '../../../store/analyticStore/useAnalyticStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import { smsOptInCheckboxVariantDefault } from '../../../store/featureStore/constants/variantDefaults';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureVariant } from '../../../store/featureStore/hooks/useFeatureVariant';
import useFeatureStore from '../../../store/featureStore/useFeatureStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useSignUpStore from '../../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();
  const {
    computed: { brand },
  } = useLeadChannelStore();

  const { t } = useTranslation();
  const { sendSignUpVariantAssignedAnalytics } = useAnalyticStore();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const {
    deriveDeliverPinParams,
    clearUserSuccessState,
    setSmsOptIn,
    userExists,
    userExistsFailedMessage,
  } = useUserStore();

  const { isReady } = useFeatureStore();

  const smsOptInCheckboxVariant = useFeatureVariant(
    FeatureFlag.ENABLE_SMS_OPT_IN_CHECKBOX,
    smsOptInCheckboxVariantDefault,
  );

  const {
    computed: {
      activationSubtitleText,
      activationTitleText,
      applicantFirstName,
      applicantLastName,
    },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    setMailCodeActivationState,
  } = useMailCodeStore();

  const {
    computed: { reviewsAsset, reviewsUrl },
  } = useSignUpStore();

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: firstName || applicantFirstName,
      lastNameValue: lastName || applicantLastName,
      primaryEmailAddressValue: primaryEmailAddress,
      primaryPhoneNumberValue: toPhoneString(primaryPhoneNumber),
      smsOptInValue: false,
    },
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: EMAIL_ADDRESS_PATTERN,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
      smsOptInValue,
    } = values;

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    // CP-6951: the lookup was rate limited, so it cannot pick a PIN purpose
    if (useUserStore.getState().userExistsFailedMessage) {
      return;
    }

    setSmsOptIn(smsOptInValue);

    setMailCodeActivationState({
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    });

    const deliverPinParams = deriveDeliverPinParams(
      userExistsException,
      DeliverPinLocation.SIGN_IN,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    );

    await deliverPin(deliverPinParams);

    const {
      computed: { isAccountOwnershipUnverified },
      deliverPinSuccess,
    } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigateWithCallback('/mail/pin-verify').then((): void => {
        clearUserSuccessState();
      });
    }

    // CP-6949: the email belongs to an existing account and the phone is not
    // on it. Verifying a new phone does not prove the account is theirs, so
    // explain the options instead of sending a PIN
    if (isAccountOwnershipUnverified) {
      // the returning-user page reads what was entered from the sign-up store
      useSignUpStore.setState({
        primaryEmailAddress: primaryEmailAddressValue,
        primaryPhoneNumber: primaryPhoneNumberValue,
      });

      // plain navigate: navigateWithCallback only navigates inside .then()
      navigate('/sign-up/returning-user');
    }
  };

  useEffect((): void => {
    if (!isReady) {
      return;
    }

    // Fire analytics event on page land signaling opt-in variant.
    sendSignUpVariantAssignedAnalytics({
      experiment_name: 'sms_opt_in_checkbox',
      variant: smsOptInCheckboxVariant,
    });
  }, [isReady, smsOptInCheckboxVariant]);

  // Because this page depends on the SMS opt-in variant to determine checkbox visibility,
  // wait until feature flags have loaded before rendering to avoid a flash of incorrect content.
  if (!isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="page mail-code-activation-page">
        <Header showLogo splitLayout />

        <main>
          <LanderImage
            alt={t('imageAlts.vehicleSUVTopView')}
            defaultImage={vehicleSUVTopView}
          />
          <form
            data-testid={locator.pages.mailCodeActivation.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.mailCodeActivation.form.h1}>
                {activationTitleText}
              </h1>
              <h2 data-testid={locator.pages.mailCodeActivation.form.h2}>
                {activationSubtitleText}
              </h2>

              {(userExistsFailedMessage ?? deliverPinFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.mailCodeActivation.alert.error}
                    severity="error"
                  >
                    {userExistsFailedMessage ?? deliverPinFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <Input
                      {...register('firstNameValue', firstNameValidation)}
                      data-testid={
                        locator.pages.mailCodeActivation.form.firstNameInput
                      }
                      error={errors.firstNameValue}
                      errorTestId={
                        locator.pages.mailCodeActivation.form
                          .firstNameInputError
                      }
                      label={t('MailCodeActivationPage.firstName')}
                    />
                  </div>

                  <div className="input-half">
                    <Input
                      {...register('lastNameValue', lastNameValidation)}
                      data-testid={
                        locator.pages.mailCodeActivation.form.lastNameInput
                      }
                      error={errors.lastNameValue}
                      errorTestId={
                        locator.pages.mailCodeActivation.form.lastNameInputError
                      }
                      label={t('MailCodeActivationPage.lastName')}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <NumberInput
                    {...register(
                      'primaryPhoneNumberValue',
                      phoneNumberValidation,
                    )}
                    autoComplete="tel-national"
                    data-testid={
                      locator.pages.mailCodeActivation.form.phoneInput
                    }
                    error={errors.primaryPhoneNumberValue}
                    errorTestId={
                      locator.pages.mailCodeActivation.form.phoneInputError
                    }
                    label={t('MailCodeActivationPage.phone')}
                    phone
                  />
                </div>

                <div>
                  <Input
                    {...register(
                      'primaryEmailAddressValue',
                      emailAddressValidation,
                    )}
                    data-testid={
                      locator.pages.mailCodeActivation.form.emailInput
                    }
                    error={errors.primaryEmailAddressValue}
                    errorTestId={
                      locator.pages.mailCodeActivation.form.emailInputError
                    }
                    label={t('MailCodeActivationPage.email')}
                  />
                </div>

                {smsOptInCheckboxVariant !== smsOptInCheckboxVariantDefault && (
                  <div style={{ marginTop: '1rem' }}>
                    <Checkbox
                      {...register('smsOptInValue')}
                      checked={watch('smsOptInValue', false)}
                      label={
                        <Trans
                          i18nKey={
                            'MailCodeActivationPage.smsOptInAcknowledgement'
                          }
                          values={{ brand }}
                        />
                      }
                      testId={
                        locator.pages.mailCodeActivation.form.smsOptInCheckbox
                      }
                      variant="sm"
                    />
                  </div>
                )}
              </div>

              <Button
                data-testid={locator.pages.mailCodeActivation.form.submitButton}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('MailCodeActivationPage.continue')}
              </Button>

              <p className="form-terms-privacy">
                <Trans
                  components={{
                    terms: (
                      <a
                        data-testid={
                          locator.pages.mailCodeActivation.form.termsLink
                        }
                        href="https://www.thesavingsgroup.com/terms-of-use/"
                        rel="noreferrer"
                        target="_blank"
                      />
                    ),
                    privacy: (
                      <a
                        data-testid={
                          locator.pages.mailCodeActivation.form.privacyLink
                        }
                        href="https://www.thesavingsgroup.com/privacy-policy/"
                        rel="noreferrer"
                        target="_blank"
                      />
                    ),
                  }}
                  i18nKey={'MailCodeActivationPage.termsPrivacy'}
                />
              </p>
              <a
                className="reviews-link"
                data-testid={locator.pages.mailCodeActivation.form.reviewsLink}
                href={reviewsUrl}
                rel="noreferrer"
                target="_blank"
              >
                <img alt={t('imageAlts.reviews')} src={reviewsAsset} />
              </a>
            </div>
            <footer data-testid={locator.pages.mailCodeActivation.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'MailCodeActivationPage';
